import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, concatLatestFrom } from '@ngrx/effects';
import { UiSettingsIdentifier, UiUserSettingsService } from '../../services/ui-user-settings.service';
import { fetch } from '@nrwl/angular';
import { SubmissionPageActionTypes, setUserSettingsSuccess } from './submissions-page.actions';
import * as SubmissionPageActions from './submissions-page.actions';
import { map, mergeMap, catchError, withLatestFrom, filter, pluck, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import { selectSubmissionsPageUserSettings } from './submissions-page.selectors';
import { Router } from '@angular/router';
import { routes } from '../../../app.routes';
import { BackendErrorService } from '../../services/backend-error.service';
import { translationKeys } from '../../core';

@Injectable()
export class SubmissionsPageEffects {
  loadSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubmissionPageActionTypes.GetUserSettings),
      fetch({
        run: ({ versionFlag }) => {
          return this.uiUserSettingsService
            .getUiSettingsFor(UiSettingsIdentifier.SubmissionsTable, versionFlag)
            .pipe(map((settings: Record<string, any>) => SubmissionPageActions.getUserSettingsSuccess({ settings })));
        },
        onError: (_, error) => {
          this.backendErrorService.handleWarning(error, translationKeys.uiUserSettingsService.msg.warning.restoringSettings);
          return SubmissionPageActions.getUserSettingsFail({ error });
        }
      })
    );
  });

  setSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubmissionPageActionTypes.SetUserSettings),
      mergeMap(({ settings, versionFlag }) =>
        this.uiUserSettingsService.saveUiSettingsFor(UiSettingsIdentifier.SubmissionsTable, settings, versionFlag).pipe(
          map(() => SubmissionPageActions.setUserSettingsSuccess({ settings })),
          catchError((error) => {
            this.backendErrorService.handleWarning(error, translationKeys.uiUserSettingsService.msg.warning.savingSettings);
            return of(SubmissionPageActions.setUserSettingsFail({ error }));
          })
        )
      )
    );
  });

  setFiltersPreferences$ = createEffect(() => {
    return this.actions$.pipe(
      concatLatestFrom(() => this.store.select(selectSubmissionsPageUserSettings)),
      filter(([action, _]: [any, Record<string, any>]) => action.type === SubmissionPageActionTypes.SetFiltersPreferences),
      mergeMap(([{ filtersPreferences }, settings]) =>
        this.uiUserSettingsService.saveUiSettingsFor(UiSettingsIdentifier.SubmissionsTable, { ...settings, filters: filtersPreferences }, '4.1.3').pipe(
          map(() => SubmissionPageActions.setUserSettingsSuccess({ settings: { ...settings, filters: filtersPreferences }, shouldNavigate: true })),
          catchError((error) => of(SubmissionPageActions.setUserSettingsFail({ error })))
        )
      )
    );
  });

  navigateToSubmissions$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SubmissionPageActionTypes.SetUserSettingsSuccess),
        pluck('shouldNavigate'),
        filter((shouldNavigate) => shouldNavigate),
        tap(() => {
          this.router.navigate([routes.submissions]);
        })
      );
    },
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private readonly uiUserSettingsService: UiUserSettingsService,
    private readonly backendErrorService: BackendErrorService,
    private readonly store: Store,
    private readonly router: Router
  ) {}
}
